/**
 * Passo de paciente do Pronto Atendimento.
 * Org → cadastro nacional → confirmação CPF → cadastro local → responsável.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { currentFeatures, oids } from "@/configs/Settings";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { usePatientOrganizationContext } from "@/contexts/PatientOrganizationContext";
import { RelatedPersonVerificationList } from "@/components/schedule/RelatedPersonVerificationList";
import { mapPatientEntryToSearchRow } from "@/components/schedule/documentReissue/reissuePatientId";
import { useRelatedPersonVerification } from "@/hooks/useRelatedPersonVerification";
import { GetPatient, PostPatient } from "@/services/fhir";
import {
  fetchPatientRelatedPersons,
  getRelatedPersonIdentifierValue,
  getRelatedPersonResourceId,
} from "@/services/fhir/relatedperson/relatedPersonVerification";
import { SearchCPF, toSerproNascimento } from "@/services/scim";
import { useFormatter } from "@/utils/useFormatter";
import { useValidate } from "@/utils/useValidate";

export interface ProntoSelectedPatient {
  reference: string;
  display: string;
  cpf: string;
  birthDate?: string;
  /** Telefone (telecom) quando disponível — WhatsApp/lembrete. */
  phone?: string;
  responsibleCpf?: string;
  responsibleName?: string;
}

interface ProntoAtendimentoPatientStepProps {
  onContinue: (patient: ProntoSelectedPatient) => void;
}

type ViewMode = "search" | "related" | "register" | "registerResponsible";
type SearchRow = ReturnType<typeof mapPatientEntryToSearchRow>;

type SerproFetchResult =
  | { status: "invalid" }
  | { status: "minor" }
  | { status: "error" }
  | { status: "ok"; data: Record<string, unknown> };

function onlyDigits(value: string): string {
  return value.replace(/\D/g, "");
}

/** CPF com 11 dígitos e dígitos verificadores válidos — gate antes de qualquer API. */
function isCpfFormatValid(cpfDigits: string): boolean {
  return cpfDigits.length === 11 && useValidate.cpfNumber(cpfDigits);
}

function isMinor(birthDate?: string) {
  if (!birthDate) return false;
  const age = dayjs().diff(dayjs(birthDate), "year");
  return age < 18;
}

function findOrgRowByCpf(rows: SearchRow[], cpfDigits: string): SearchRow | undefined {
  return rows.find((row) => onlyDigits(row.patientId || "") === cpfDigits);
}

type PersonFormState = {
  cpf: string;
  name: string;
  birth: string;
  gender: string;
  phone: string;
  serpro: Record<string, unknown> | null;
};

const emptyPersonForm = (): PersonFormState => ({
  cpf: "",
  name: "",
  birth: "",
  gender: "",
  phone: "",
  serpro: null,
});

export function ProntoAtendimentoPatientStep({
  onContinue,
}: ProntoAtendimentoPatientStepProps) {
  const { organization } = useOrganizationContext();
  const { patients, patientOrganizationLoading, refetchPatientOrganization } =
    usePatientOrganizationContext();

  const [search, setSearch] = useState("");
  const [view, setView] = useState<ViewMode>("search");
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState<ProntoSelectedPatient | null>(null);
  const [activeRelated, setActiveRelated] = useState<any[]>([]);
  const [inactiveRelated, setInactiveRelated] = useState<any[]>([]);
  const [chosenRelatedCpf, setChosenRelatedCpf] = useState("");
  const [informResponsible, setInformResponsible] = useState(false);
  const [manualResponsibleCpf, setManualResponsibleCpf] = useState("");
  /** Menor de idade detectado na confirmação do CPF — usado após cadastro. */
  const [pendingMinorResponsible, setPendingMinorResponsible] = useState(false);

  const [patientForm, setPatientForm] = useState<PersonFormState>(emptyPersonForm);
  const [responsibleForm, setResponsibleForm] =
    useState<PersonFormState>(emptyPersonForm);

  const dismissedRelatedIdsRef = useRef<string[]>([]);
  /** Evita repetir lookup para o mesmo CPF já consultado. */
  const lastAutoLookupCpfRef = useRef("");
  /** Evita toast repetido para o mesmo CPF inválido. */
  const lastInvalidCpfToastRef = useRef("");

  const rows = useMemo(
    () => (patients || []).map(mapPatientEntryToSearchRow),
    [patients],
  );

  const query = search.trim();
  const queryDigits = onlyDigits(query);

  const searchResults = useMemo(() => {
    if (!query) return [];
    const qLower = query.toLowerCase();
    return rows.filter((row) => {
      const idDigits = row.identifier?.replace(/[^a-zA-Z0-9]/g, "") || "";
      return (
        row.name.toLowerCase().includes(qLower) ||
        (queryDigits.length > 0 && idDigits.includes(queryDigits))
      );
    });
  }, [rows, query, queryDigits]);

  const hasSearched = query.length > 0;
  const notFoundInOrg = hasSearched && searchResults.length === 0;
  const cpfLookupReady =
    queryDigits.length === 11 && isCpfFormatValid(queryDigits);

  const loadRelated = useCallback(async (cpf: string) => {
    if (!currentFeatures.defaultApp) {
      setActiveRelated([]);
      setInactiveRelated([]);
      return;
    }
    try {
      const { activeRelated: active, inactiveRelated: inactive } =
        await fetchPatientRelatedPersons(cpf, dismissedRelatedIdsRef.current);
      setActiveRelated(active);
      setInactiveRelated(inactive);
    } catch {
      setActiveRelated([]);
      setInactiveRelated([]);
    }
  }, []);

  const {
    handleVerify,
    handleDeny,
    isRowLoading,
    isVerifyLoading,
    isDenyLoading,
  } = useRelatedPersonVerification({
    patientId: selected?.cpf || "",
    onAfterVerify: async () => {
      if (selected?.cpf) await loadRelated(selected.cpf);
    },
    onAfterDeny: async (_entry, resourceId) => {
      dismissedRelatedIdsRef.current = [
        ...dismissedRelatedIdsRef.current,
        resourceId,
      ];
      if (selected?.cpf) await loadRelated(selected.cpf);
    },
  });

  const buildPatientFromResource = (
    resource: fhir5.Patient,
  ): ProntoSelectedPatient => {
    const id = resource.id || "";
    const cpf =
      currentFeatures.defaultApp && id.includes("-")
        ? id.split("-").slice(1).join("-")
        : onlyDigits(resource.identifier?.[0]?.value || "");
    const name =
      resource.name?.find((n) => n.use === "official" || n.use === "usual")
        ?.text ||
      resource.name?.[0]?.text ||
      "";
    return {
      reference: `Patient/${id}`,
      display: useFormatter.formatNameDisplay(name),
      cpf: onlyDigits(cpf),
      birthDate: resource.birthDate,
      phone: resource.telecom?.find((t) => t.system === "phone")?.value,
    };
  };

  const buildPatientFromOrgRow = (row: SearchRow): ProntoSelectedPatient => {
    const patient: ProntoSelectedPatient = {
      reference: `Patient/${row.patientResourceId || `${oids.cpf}-${row.patientId}`}`,
      display: row.name,
      cpf: row.patientId,
    };
    const entry = patients?.find(
      (e) =>
        e.resource?.id === row.patientResourceId ||
        e.resource?.id?.endsWith(`-${row.patientId}`),
    );
    if (entry?.resource?.birthDate) {
      patient.birthDate = entry.resource.birthDate;
    }
    const phone = entry?.resource?.telecom?.find((t) => t.system === "phone")
      ?.value;
    if (phone) patient.phone = phone;
    return patient;
  };

  const proceedToRelatedOrContinue = async (patient: ProntoSelectedPatient) => {
    setSelected(patient);
    setChosenRelatedCpf("");
    setInformResponsible(pendingMinorResponsible);
    setManualResponsibleCpf("");
    setLoading(true);
    try {
      await loadRelated(patient.cpf);
      setView("related");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFromList = async (row: SearchRow) => {
    await proceedToRelatedOrContinue(buildPatientFromOrgRow(row));
  };

  const applySerproToForm = (
    data: Record<string, unknown>,
    target: "patient" | "responsible",
  ) => {
    const patch: Partial<PersonFormState> = {
      serpro: data,
      name: (data.nome as string) || "",
    };
    const nascimento = data.nascimento as string | undefined;
    if (nascimento) {
      const bd = dayjs(nascimento, "DDMMYYYY");
      if (bd.isValid()) patch.birth = bd.format("YYYY-MM-DD");
    }
    if (target === "patient") {
      setPatientForm((prev) => ({ ...prev, ...patch }));
    } else {
      setResponsibleForm((prev) => ({ ...prev, ...patch }));
    }
  };

  /** Confirma CPF na Receita (V3: CPF + nascimento). */
  const fetchSerproForCpf = async (
    cpf: string,
    nascimentoRaw: string,
    target: "patient" | "responsible",
  ): Promise<SerproFetchResult> => {
    if (!isCpfFormatValid(cpf)) {
      return { status: "invalid" };
    }

    const nascimento = toSerproNascimento(nascimentoRaw);
    if (!nascimento) {
      return { status: "error" };
    }

    try {
      const serproSearch = await SearchCPF(cpf, nascimento);
      if (serproSearch?.status === 400) {
        return { status: "invalid" };
      }
      if (serproSearch?.status === 422 || serproSearch?.status === 451) {
        if (target === "responsible") {
          return { status: "minor" };
        }
        return { status: "minor", data: serproSearch.data || {} };
      }
      if (
        serproSearch?.data &&
        typeof serproSearch.data === "object" &&
        (serproSearch.data as Record<string, unknown>).nome
      ) {
        return {
          status: "ok",
          data: serproSearch.data as Record<string, unknown>,
        };
      }
      return { status: "error" };
    } catch {
      return { status: "error" };
    }
  };

  /**
   * Ordem: org → cadastro nacional → confirmação CPF → formulário de cadastro.
   * Nunca abre cadastro para CPF inválido ou antes da confirmação.
   */
  const lookupCpfThenResolveOrRegister = useCallback(
    async (cpf: string) => {
      if (!isCpfFormatValid(cpf)) {
        toast.warn("CPF inválido. Confira os números digitados.");
        lastAutoLookupCpfRef.current = "";
        setView("search");
        return;
      }

      setLoading(true);
      setView("search");

      try {
        const orgRow = findOrgRowByCpf(rows, cpf);
        if (orgRow) {
          toast.success("Paciente encontrado.");
          await handleSelectFromList(orgRow);
          return;
        }

        try {
          const response = await GetPatient({ identifier: cpf });
          if (response?.data) {
            toast.success("Paciente encontrado no cadastro.");
            await proceedToRelatedOrContinue(
              buildPatientFromResource(response.data),
            );
            return;
          }
        } catch {
          // Não encontrado no cadastro nacional — abre formulário (Serpro V3 exige nascimento)
        }

        setPendingMinorResponsible(false);
        setPatientForm({
          ...emptyPersonForm(),
          cpf: useFormatter.formatCPF(cpf),
        });
        setView("register");
        toast.info(
          "Não encontramos este paciente aqui. Informe a data de nascimento para confirmar o CPF e cadastrar.",
        );
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, patients, organization?.id],
  );

  // CPF completo, válido e ausente na org → lookup automático
  useEffect(() => {
    if (view !== "search") return;
    if (patientOrganizationLoading) return;

    if (queryDigits.length !== 11) {
      lastAutoLookupCpfRef.current = "";
      lastInvalidCpfToastRef.current = "";
      return;
    }

    if (!isCpfFormatValid(queryDigits)) {
      if (lastInvalidCpfToastRef.current !== queryDigits) {
        lastInvalidCpfToastRef.current = queryDigits;
        toast.warn("CPF inválido. Confira os números digitados.");
      }
      lastAutoLookupCpfRef.current = "";
      return;
    }

    if (searchResults.length > 0) return;

    if (lastAutoLookupCpfRef.current === queryDigits) return;
    lastAutoLookupCpfRef.current = queryDigits;

    void lookupCpfThenResolveOrRegister(queryDigits);
  }, [
    view,
    queryDigits,
    searchResults.length,
    patientOrganizationLoading,
    lookupCpfThenResolveOrRegister,
  ]);

  const handleCadastrarPaciente = async () => {
    if (queryDigits.length !== 11) {
      toast.info("Digite o CPF completo, com 11 números.");
      return;
    }
    if (!isCpfFormatValid(queryDigits)) {
      toast.warn("CPF inválido. Confira os números digitados.");
      return;
    }
    lastAutoLookupCpfRef.current = queryDigits;
    await lookupCpfThenResolveOrRegister(queryDigits);
  };

  type PostPersonResult = { patient: ProntoSelectedPatient; isNew: boolean };

  const postPerson = async (
    form: PersonFormState,
    options?: { skipOrgCheck?: boolean },
  ): Promise<PostPersonResult | null> => {
    const cpf = onlyDigits(form.cpf);
    if (!isCpfFormatValid(cpf)) {
      toast.info("CPF inválido. Confira os números digitados.");
      return null;
    }

    const name = ((form.serpro?.nome as string) || form.name).trim();
    if (!name) {
      toast.info("Informe o nome completo.");
      return null;
    }
    if (!form.gender) {
      toast.info("Informe o sexo.");
      return null;
    }
    if (!form.phone.replace(/\D/g, "")) {
      toast.info("Informe o telefone.");
      return null;
    }

    if (!options?.skipOrgCheck) {
      const orgRow = findOrgRowByCpf(rows, cpf);
      if (orgRow) {
        toast.info("Este paciente já está cadastrado.");
        return { patient: buildPatientFromOrgRow(orgRow), isNew: false };
      }
    }

    try {
      const existing = await GetPatient({ identifier: cpf });
      if (existing?.data) {
        toast.info("Este paciente já está cadastrado.");
        return {
          patient: buildPatientFromResource(existing.data),
          isNew: false,
        };
      }
    } catch {
      // Segue cadastro
    }

    const nascimento = form.serpro?.nascimento as string | undefined;
    const body = {
      name,
      identifier: (form.serpro?.ni as string) || cpf,
      birthDate: dayjs(nascimento, "DDMMYYYY").isValid()
        ? dayjs(nascimento, "DDMMYYYY").format("YYYY-MM-DD")
        : form.birth
          ? dayjs(form.birth).format("YYYY-MM-DD")
          : undefined,
      phoneNumber: form.phone.replace(/\D/g, ""),
      managingOrganization: organization?.id,
      gender: form.gender,
    };

    const mpiResponse = await PostPatient(body);
    if (!mpiResponse?.data) {
      toast.error("Não foi possível concluir o cadastro. Tente novamente.");
      return null;
    }
    return {
      patient: buildPatientFromResource(mpiResponse.data),
      isNew: true,
    };
  };

  const handleRegisterPatient = async () => {
    setLoading(true);
    try {
      let form = patientForm;

      if (!form.serpro) {
        if (!form.birth) {
          toast.info("Informe a data de nascimento para validar o CPF.");
          return;
        }
        const serproResult = await fetchSerproForCpf(
          onlyDigits(form.cpf),
          form.birth,
          "patient",
        );
        if (serproResult.status === "invalid") {
          toast.warn("CPF inválido. Confira os números digitados.");
          return;
        }
        if (serproResult.status === "error") {
          toast.info("Não foi possível confirmar o CPF. Tente novamente.");
          return;
        }
        setPendingMinorResponsible(serproResult.status === "minor");
        if (serproResult.status === "minor") {
          toast.warn(
            "Paciente menor de idade — será necessário informar um responsável.",
          );
          form = { ...form, serpro: serproResult.data || {} };
        } else if (serproResult.status === "ok" && serproResult.data) {
          const nascimento = serproResult.data.nascimento as string | undefined;
          form = {
            ...form,
            serpro: serproResult.data,
            name: (serproResult.data.nome as string) || form.name,
            birth:
              nascimento && dayjs(nascimento, "DDMMYYYY").isValid()
                ? dayjs(nascimento, "DDMMYYYY").format("YYYY-MM-DD")
                : form.birth,
          };
        }
        setPatientForm(form);
      }

      const result = await postPerson(form);
      if (!result) return;

      if (result.isNew) {
        toast.success("Paciente cadastrado com sucesso.");
      }

      refetchPatientOrganization();
      await proceedToRelatedOrContinue(result.patient);
    } catch {
      toast.error("Não foi possível concluir o cadastro. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const openRegisterResponsible = async (cpfFormatted: string) => {
    const digits = onlyDigits(cpfFormatted);
    if (!isCpfFormatValid(digits)) {
      toast.warn("CPF inválido. Confira os números digitados.");
      return;
    }

    setResponsibleForm({
      ...emptyPersonForm(),
      cpf: useFormatter.formatCPF(digits),
    });
    setView("registerResponsible");
    toast.info(
      "Informe a data de nascimento do responsável para validar o CPF e cadastrar.",
    );
  };

  const handleRegisterResponsible = async () => {
    setLoading(true);
    try {
      let form = responsibleForm;

      if (!form.serpro) {
        if (!form.birth) {
          toast.info("Informe a data de nascimento para validar o CPF.");
          return;
        }
        const serproResult = await fetchSerproForCpf(
          onlyDigits(form.cpf),
          form.birth,
          "responsible",
        );
        if (serproResult.status === "invalid") {
          toast.warn("CPF inválido. Confira os números digitados.");
          return;
        }
        if (serproResult.status === "minor") {
          toast.warn(
            "O CPF informado pertence a um menor. Informe um responsável maior de idade.",
          );
          return;
        }
        if (serproResult.status === "error") {
          toast.info(
            "Não foi possível confirmar o CPF. Preencha os dados manualmente.",
          );
        } else if (serproResult.status === "ok" && serproResult.data) {
          const nascimento = serproResult.data.nascimento as string | undefined;
          form = {
            ...form,
            serpro: serproResult.data,
            name: (serproResult.data.nome as string) || form.name,
            birth:
              nascimento && dayjs(nascimento, "DDMMYYYY").isValid()
                ? dayjs(nascimento, "DDMMYYYY").format("YYYY-MM-DD")
                : form.birth,
          };
          setResponsibleForm(form);
        }
      }

      const result = await postPerson(form, { skipOrgCheck: true });
      if (!result) return;
      if (isMinor(result.patient.birthDate)) {
        toast.warn("O responsável deve ser maior de idade.");
        return;
      }
      if (result.isNew) {
        toast.success("Responsável cadastrado com sucesso.");
      }
      refetchPatientOrganization();
      if (!selected) return;
      onContinue({
        ...selected,
        responsibleCpf: result.patient.cpf,
        responsibleName: result.patient.display,
      });
    } catch {
      toast.error("Não foi possível cadastrar o responsável.");
    } finally {
      setLoading(false);
    }
  };

  const handleFinishRelated = async () => {
    if (!selected) return;

    let responsibleCpf = "";
    let responsibleName = "";

    if (chosenRelatedCpf) {
      const entry = activeRelated.find(
        (e) =>
          useFormatter.formatCPF(getRelatedPersonIdentifierValue(e) || "") ===
            chosenRelatedCpf ||
          onlyDigits(getRelatedPersonIdentifierValue(e) || "") ===
            onlyDigits(chosenRelatedCpf),
      );
      responsibleCpf = onlyDigits(
        getRelatedPersonIdentifierValue(entry) || chosenRelatedCpf,
      );
      responsibleName = entry?.resource?.name?.[0]?.text || "";
    } else if (
      informResponsible ||
      (isMinor(selected.birthDate) && activeRelated.length === 0)
    ) {
      responsibleCpf = onlyDigits(manualResponsibleCpf);
      if (!isCpfFormatValid(responsibleCpf)) {
        toast.info("Digite o CPF completo do responsável, com 11 números.");
        return;
      }

      setLoading(true);
      try {
        const resp = await GetPatient({ identifier: responsibleCpf });
        if (resp?.data) {
          const built = buildPatientFromResource(resp.data);
          if (isMinor(built.birthDate)) {
            toast.warn("O responsável deve ser maior de idade.");
            return;
          }
          responsibleName = built.display;
        } else {
          await openRegisterResponsible(manualResponsibleCpf);
          return;
        }
      } catch {
        await openRegisterResponsible(manualResponsibleCpf);
        return;
      } finally {
        setLoading(false);
      }
    } else if (isMinor(selected.birthDate) && !chosenRelatedCpf) {
      toast.warn(
        "Paciente menor de idade: selecione ou informe um responsável.",
      );
      return;
    }

    if (activeRelated.length > 0 && !chosenRelatedCpf && !informResponsible) {
      toast.info("Selecione o responsável legal.");
      return;
    }

    onContinue({
      ...selected,
      responsibleCpf: responsibleCpf || undefined,
      responsibleName: responsibleName || undefined,
    });
  };

  const renderPersonForm = (
    form: PersonFormState,
    setForm: React.Dispatch<React.SetStateAction<PersonFormState>>,
    title: string,
    onBack: () => void,
    onSubmit: () => void,
  ) => (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <TextField
        variant="filled"
        size="small"
        fullWidth
        label="CPF"
        value={form.cpf}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            cpf: useFormatter.formatCPF(e.target.value),
          }))
        }
      />
      <TextField
        variant="filled"
        size="small"
        fullWidth
        label="Nome completo"
        value={form.name}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, name: e.target.value }))
        }
        disabled={!!form.serpro?.nome}
      />
      <TextField
        variant="filled"
        size="small"
        fullWidth
        label="Data de nascimento"
        type="date"
        value={form.birth}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, birth: e.target.value }))
        }
        InputLabelProps={{ shrink: true }}
        disabled={
          !!form.serpro?.nascimento &&
          dayjs(form.serpro.nascimento as string, "DDMMYYYY").isValid()
        }
      />
      <TextField
        select
        variant="filled"
        size="small"
        fullWidth
        label="Sexo"
        value={form.gender}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, gender: e.target.value }))
        }
      >
        <MenuItem value="male">Masculino</MenuItem>
        <MenuItem value="female">Feminino</MenuItem>
      </TextField>
      <TextField
        variant="filled"
        size="small"
        fullWidth
        label="Telefone"
        value={form.phone}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, phone: e.target.value }))
        }
      />
      <Stack direction="row" spacing={1}>
        <Button
          onClick={onBack}
          disabled={loading}
          sx={{ textTransform: "none" }}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={() => void onSubmit()}
          disabled={loading}
          sx={{ textTransform: "none" }}
        >
          {loading ? "Salvando..." : "Cadastrar e continuar"}
        </Button>
      </Stack>
    </Stack>
  );

  if (view === "register") {
    return renderPersonForm(
      patientForm,
      setPatientForm,
      "Cadastrar paciente",
      () => {
        setView("search");
        setPatientForm(emptyPersonForm());
        setPendingMinorResponsible(false);
        lastAutoLookupCpfRef.current = "";
      },
      handleRegisterPatient,
    );
  }

  if (view === "registerResponsible") {
    return renderPersonForm(
      responsibleForm,
      setResponsibleForm,
      "Cadastrar responsável",
      () => {
        setView("related");
        setResponsibleForm(emptyPersonForm());
      },
      handleRegisterResponsible,
    );
  }

  if (view === "related" && selected) {
    return (
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Box>
          <Typography variant="body1" fontWeight={600}>
            {selected.display}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            CPF: {useFormatter.formatCPF(selected.cpf)}
          </Typography>
        </Box>

        {loading ? (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <>
            {currentFeatures.defaultApp && (
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">Responsável legal</Typography>
                <RelatedPersonVerificationList
                  entries={inactiveRelated}
                  isRowLoading={isRowLoading}
                  isVerifyLoading={isVerifyLoading}
                  isDenyLoading={isDenyLoading}
                  onVerify={handleVerify}
                  onDeny={handleDeny}
                  title="Pendentes de verificação"
                />

                {activeRelated.length > 0 && (
                  <TextField
                    select
                    variant="filled"
                    size="small"
                    fullWidth
                    label="Selecionar responsável"
                    value={chosenRelatedCpf}
                    onChange={(e) => {
                      setChosenRelatedCpf(e.target.value);
                      setInformResponsible(false);
                    }}
                  >
                    {activeRelated.map((entry) => {
                      const id = getRelatedPersonResourceId(entry) || "";
                      const cpfVal =
                        getRelatedPersonIdentifierValue(entry) || "";
                      return (
                        <MenuItem
                          key={id}
                          value={useFormatter.formatCPF(cpfVal)}
                        >
                          {entry.resource?.name?.[0]?.text} —{" "}
                          {useFormatter.formatCPF(cpfVal)}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                )}

                {(activeRelated.length === 0 || informResponsible) && (
                  <>
                    {activeRelated.length > 0 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={informResponsible}
                            onChange={(e) => {
                              setInformResponsible(e.target.checked);
                              if (e.target.checked) setChosenRelatedCpf("");
                            }}
                          />
                        }
                        label="Informar outro responsável"
                      />
                    )}
                    {activeRelated.length === 0 &&
                      !isMinor(selected.birthDate) && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={informResponsible}
                              onChange={(e) =>
                                setInformResponsible(e.target.checked)
                              }
                            />
                          }
                          label="Informar responsável legal"
                        />
                      )}
                    {(informResponsible ||
                      (isMinor(selected.birthDate) &&
                        activeRelated.length === 0)) && (
                      <TextField
                        variant="filled"
                        size="small"
                        fullWidth
                        label="CPF do responsável"
                        value={manualResponsibleCpf}
                        onChange={(e) =>
                          setManualResponsibleCpf(
                            useFormatter.formatCPF(e.target.value),
                          )
                        }
                        helperText="Se não encontrarmos o responsável, você poderá cadastrá-lo em seguida."
                      />
                    )}
                  </>
                )}
              </Stack>
            )}

            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => {
                  setView("search");
                  setSelected(null);
                  setPendingMinorResponsible(false);
                  lastAutoLookupCpfRef.current = "";
                }}
                sx={{ textTransform: "none" }}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                onClick={() => void handleFinishRelated()}
                sx={{ textTransform: "none" }}
              >
                Continuar
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Busque por nome ou CPF. Com CPF completo e válido, se não estiver na
        clínica buscamos no cadastro nacional automaticamente.
      </Typography>

      <TextField
        variant="filled"
        label="Buscar por nome ou CPF"
        size="small"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Ex.: Maria ou 000.000.000-00"
        autoFocus
        helperText={
          queryDigits.length > 0 && queryDigits.length < 11
            ? "Digite os 11 números do CPF para buscar ou cadastrar."
            : queryDigits.length === 11 && !isCpfFormatValid(queryDigits)
              ? "CPF inválido. Confira os números digitados."
              : undefined
        }
        error={queryDigits.length === 11 && !isCpfFormatValid(queryDigits)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {patientOrganizationLoading && (
        <Stack alignItems="center" py={2}>
          <CircularProgress size={28} />
        </Stack>
      )}

      {!patientOrganizationLoading && !hasSearched && (
        <Typography variant="body2" color="text.secondary">
          Digite o nome ou CPF para buscar.
        </Typography>
      )}

      {!patientOrganizationLoading && searchResults.length > 0 && (
        <Paper
          variant="outlined"
          sx={{ borderRadius: 2, maxHeight: 240, overflow: "auto" }}
        >
          <List dense disablePadding>
            {searchResults.slice(0, 50).map((row) => (
              <ListItemButton
                key={row.patientResourceId || row.patientId}
                onClick={() => void handleSelectFromList(row)}
                disabled={loading}
              >
                <ListItemText
                  primary={row.name}
                  secondary={row.identifier || undefined}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      {notFoundInOrg && (
        <Stack spacing={1.5}>
          {loading && cpfLookupReady ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Buscando paciente…
              </Typography>
            </Stack>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Nenhum paciente na clínica com “{query}”.
              </Typography>
              {queryDigits.length !== 11 && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    Digite os 11 números do CPF para buscar ou cadastrar.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => void handleCadastrarPaciente()}
                    disabled={loading}
                    sx={{ textTransform: "none", alignSelf: "flex-start" }}
                  >
                    Cadastrar paciente
                  </Button>
                </>
              )}
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
}
